import { QueryCache, QueryClient } from '@tanstack/react-query'
import { isPboScreenActive, notice } from '@/components/pbo/notice'
import { apiErrorText } from '@/components/pbo/apiErrorText'
import MAlert from '@/utils/modal/MAlert'
import { Utils } from '@/utils/Utils'

const QUERY_FAILED_MESSAGE = '데이터를 가져올 수 없습니다. 다시 시도해 주세요.'

/** 실패 알럿 문구 — BE meta.message → detail → message 순. Utils.getApiErrMessage 는 HTML 별표를 덧붙여 그대로 쓸 수 없다. */
const toFailureMessage = (e: unknown): string => apiErrorText(e, QUERY_FAILED_MESSAGE) // 서버 문구 추출은 apiErrorText 하나로(여러 건은 줄바꿈 — 알럿은 pre-line)

/**
 * 조회를 **시작한** 화면이 2.0 이었는가 — 실패 시점에 재면 응답 전에 1.0 탭으로 옮긴 경우 옛 알럿으로 떨어진다(#899 리뷰,
 * useDownloadExcel 의 fromPboScreen 과 같은 이유). QueryCache 의 fetch 액션 때 그 시점 화면을 쿼리별로 적어 둔다.
 */
const startedOnPboScreen = new WeakMap<object, boolean>()

const queryCache = new QueryCache({
  onError: (e, query) => {
    if (query.meta?.noErrorMessage) {
      return
    }
    // pbo-core(2.0 신규 디자인) 화면의 조회 실패는 옛 mwms-ds 알럿 대신 새 디자인 알럿으로 알린다(2.0 안내 기준 — 실패=알럿, notice.ts).
    //  쿼리가 meta 로 고른다(옵션 이름 errorToast 는 옛 이름 그대로 — 이제 토스트가 아니라 알럿이다).
    //  meta 를 안 단 공용 조회 훅(옛 화면과 같이 쓰는 서비스 훅)도 2.0 화면에서 시작한 조회면 새 알럿으로 — 옵션을 빠뜨려 옛 알럿이 뜨던 것(재고현황 조회 실패, 2026-10-02 QA).
    if (query.meta?.errorToast || (startedOnPboScreen.get(query) ?? isPboScreenActive())) {
      notice.failure(toFailureMessage(e))
      return
    }
    MAlert.show(Utils.getApiErrMessage(e, QUERY_FAILED_MESSAGE))
  },
})
queryCache.subscribe((event) => {
  if (event.type === 'updated' && event.action.type === 'fetch') startedOnPboScreen.set(event.query, isPboScreenActive())
})

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 0,
      refetchOnWindowFocus: false,
      // 조회 결과를 로컬 rows 로 옮겨 놓고 셀 편집·엑셀 업로드분을 그 위에 쌓는 화면이 여럿이다.
      // 재연결 자동 재조회가 켜져 있으면(react-query 기본값) 네트워크가 잠깐 끊겼다 붙기만
      // 해도 재조회가 돌아 rows 가 서버 응답으로 덮이고 저장 전 입력이 통째로 날아간다.
      // 사용자 조작 없이 나는 사고라 화면에서 알아챌 방법도 없다 — 전역으로 막는다.
      // 재조회가 실제로 필요한 화면은 refetchInterval 등 명시적 수단을 쓴다.
      refetchOnReconnect: false,
    },
  },
  queryCache,
})
